import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import apiClient from "../../services/apiClient";

const ImageUploadBottomSheet = ({ onClose, ticketId, onSelect }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    // localStorage.removeItem("galleryImages");
    const myList = localStorage.getItem("galleryImages");
    const newList = myList ? JSON.parse(myList) : [];

    setGalleryImages(newList);
  }, []);

  const handleImageClick = (imageUrl) => {
    if (selectedImages.includes(imageUrl)) {
      setSelectedImages(selectedImages.filter((img) => img !== imageUrl));
    } else {
      setSelectedImages([...selectedImages, imageUrl]);
    }
  };

  const uploadToAPI = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("ticketId", ticketId);

    try {
      const response = await apiClient.post("/chat/upload", formData);
      if (!response.data.success) {
        throw new Error("Upload failed");
      }

      const data = response;
      console.log("Upload successful:", data.data.data);
      setGalleryImages([...galleryImages, data.data.data]);

      let myList = localStorage.getItem("galleryImages");
      myList = myList ? JSON.parse(myList) : [];
      myList.push(data.data.data);
      localStorage.setItem("galleryImages", JSON.stringify(myList));

      return data;
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
      throw error;
    }
  };

  const handleCameraCapture = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);

      try {
        // Upload to API
        await uploadToAPI(file);

        // After successful upload, add to preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImages([...selectedImages, e.target.result]);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Upload error:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    setUploading(true);

    try {
      // Upload all files to API
      const uploadPromises = files.map((file) => uploadToAPI(file));
      const response = await Promise.all(uploadPromises);

      // After successful uploads, add to gallery
      const newImages = [];
      for (const file of files) {
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (e) => {
            newImages.push(response[0].data.data);
            resolve();
          };
          reader.readAsDataURL(file);
        });
      }

      setGalleryImages([...galleryImages, ...newImages]);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const sendImages = () => {
    if (selectedImages.length > 0) {
      console.log("Sending images:", selectedImages);
      onSelect(selectedImages);
      setSelectedImages([]);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end"
      onClick={onClose}
    >
      <div
        className="w-full bg-green-700 rounded-t-3xl p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold text-lg">Select Images</h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-green-600 p-1 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Upload Status */}
        {uploading && (
          <div className="mb-4 bg-yellow-500 text-white px-4 py-2 rounded-lg text-center">
            Uploading images...
          </div>
        )}

        {/* Gallery Grid */}
        <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto mb-4">
          {/* Camera placeholder */}
          <div
            // onClick={() => cameraInputRef.current?.click()}
            onClick={() => galleryInputRef.current?.click()}
            className="aspect-square bg-black rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-700 transition-colors"
          >
            <Camera className="w-8 h-8 text-white" />
          </div>

          {/* Gallery Images */}
          {galleryImages.map((imageUrl, index) => {
            // console.log(imageUrl);
            return (
              <div
                key={index}
                onClick={() => handleImageClick(imageUrl)}
                className={`aspect-square rounded-lg overflow-hidden cursor-pointer relative ${
                  selectedImages.includes(imageUrl)
                    ? "ring-4 ring-blue-500"
                    : ""
                }`}
              >
                <img
                  src={imageUrl.fileUrl}
                  alt={`Gallery ${index}`}
                  className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                />
                {selectedImages.includes(imageUrl) && (
                  <div className="absolute top-1 right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-bold">
                      {selectedImages.indexOf(imageUrl) + 1}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Send Button */}
        {selectedImages.length > 0 && (
          <button
            onClick={sendImages}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Send {selectedImages.length} image
            {selectedImages.length > 1 ? "s" : ""}
          </button>
        )}

        {/* Hidden Input Elements */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraCapture}
          className="hidden"
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleGalleryUpload}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ImageUploadBottomSheet;
